import React, { useState } from "react";
import './TallySoftware.css';
import { FaCheckCircle, FaRocket, FaCloudUploadAlt, FaWhatsapp, FaChartLine, FaCreditCard, FaSyncAlt } from 'react-icons/fa';
import { useNavigate } from "react-router-dom";

const TallySoftware = () => {
  const [selectedYearTssSilver, setSelectedYearTssSilver] = useState("1");
  const [selectedYearTssGold, setSelectedYearTssGold] = useState("1");
  const [selectedYearTssAuditor, setSelectedYearTssAuditor] = useState("1");
  const navigate = useNavigate();

  const handleBuyNowClick = () => navigate("/contact");

  return (
    <div className="tally-page-wrapper">
      {/* ── Hero Section ── */}
      <section className="tally-hero">
        <div className="tally-hero-main">
          <div className="tally-hero-content">
            <h1>Tally Software Services (TSS)</h1>
            <p>
              Enhance your TallyPrime experience with cutting-edge technology, 
              statutory compliance, and seamless business connectivity.
            </p>
          </div>
          <div className="tally-hero-visual">
            <img src="/images/tss.webp" alt="TSS Subscription" className="tss-hero-img" />
          </div>
        </div>
      </section>

      {/* ── Features Section ── */}
      <div className="tss-features-container">
        <FeatureRow 
          title="What is TSS?"
          imageSrc="/images/tss-right.webp"
          points={[
            "TSS is a comprehensive software subscription that adds immense value to your TallyPrime.",
            "Continuous product upgrades and updates for the latest technology.",
            "Functionalities driven by connectivity like online data exchange and remote access.",
            "Seamless banking and payment services to enhance performance."
          ]}
        />

        <FeatureRow 
          title="Automated GST Returns"
          imageSrc="/images/tallysoftimg/connected-gst-experience-to-upload-and-file-gst-returns (1).svg"
          points={[
            "Automate and simplify GST return filing with TallyPrime.",
            "Generate GSTR-1 and GSTR-3B instantly within the software.",
            "Download reconciliation reports and validate compliance easily.",
            "Share accurate GST data with your CA with a single click."
          ]}
          icon={<FaCloudUploadAlt />}
        />

        <FeatureRow 
          title="Instant E-Invoices & E-Way Bills"
          imageSrc="/images/tallysoftimg/CS-e-way-bills-instantly (1).png"
          points={[
            "Instantly generate e-invoices and e-way bills without leaving Tally.",
            "Validate documents instantly and ensure full GST compliance.",
            "Gain better visibility into billing and supply chain requirements.",
            "Seamless integration for high-speed documentation."
          ]}
          icon={<FaRocket />}
        />

        <FeatureRow 
          title="TallyPrime with WhatsApp"
          imageSrc="/images/tallysoftimg/tss-image (1).png"
          points={[
            "Directly share invoices and reports via WhatsApp.",
            "Improve customer service with instant communication.",
            "Send delivery notes and receipts to clients immediately.",
            "Keep your business connected with the most popular messaging tool."
          ]}
          icon={<FaWhatsapp />}
        />

        <FeatureRow 
          title="Mobile Business Reports"
          imageSrc="/images/tallysoftimg/Business-Reports-on-web-browser-1.png"
          points={[
            "Access critical business reports on any device, anywhere.",
            "Real-time insights into your financial health on the go.",
            "Secure browser-based access to your Tally data.",
            "Make informed decisions even when you're away from the office."
          ]}
          icon={<FaChartLine />}
        />

        <FeatureRow 
          title="Digital Payment Requests"
          imageSrc="/images/tallysoftimg/Payment-Gateway-Integration-1-1-1.png"
          points={[
            "Integrate with payment gateways like PayU and Razorpay.",
            "Generate and share payment links/QR codes via email and SMS.",
            "Support for UPI payments for faster collections.",
            "Track payments directly within your Tally system."
          ]}
          icon={<FaCreditCard />}
        />

        <FeatureRow 
          title="Online Data Synchronization"
          imageSrc="/images/tallyimg/4.webp"
          points={[
            "Synchronize data across branches, factories, and warehouses effortlessly.",
            "Consolidate head office and branch books with one click.",
            "Greater control over data direction and synchronization timing.",
            "No need for a continuous live connection with on-demand sync."
          ]}
          icon={<FaSyncAlt />}
        />
      </div>

      {/* ── Contact for Pricing Section ── */}
      <section className="tss-pricing-section">
        <div className="tss-pricing-header">
          <h2>TSS Subscription Plans</h2>
          <p>We offer customized subscription plans for Tally Software Services (TSS) based on your business requirements.</p>
        </div>

        <div className="tss-edition-showcase">
          {[
            { name: "TSS Silver", type: "Single User Edition", img: "/images/tss/silver.png", color: "#94a3b8" },
            { name: "TSS Gold", type: "Multi-User Edition", img: "/images/tss/gold.png", color: "#f59e0b", featured: true },
            { name: "TSS Auditor", type: "Auditor Edition", img: "/images/tss/auditor.png", color: "#008080" },
          ].map(ed => (
            <div className={`tss-edition-card ${ed.featured ? 'tss-featured' : ''}`} key={ed.name}>
              {ed.featured && <div className="tss-popular-tag">Most Popular</div>}
              <div className="tss-edition-img-wrap">
                <img src={ed.img} alt={ed.name} />
              </div>
              <div className="tss-edition-info">
                <span className="tss-edition-type" style={{ color: ed.color }}>{ed.type}</span>
                <h3 style={{ color: ed.color }}>{ed.name}</h3>
              </div>
            </div>
          ))}
        </div>

        <div className="tss-pricing-cta">
          <p>Get in touch with our Tally experts for the best subscription quotes, renewal support, and seamless onboarding.</p>
          <button className="tss-contact-btn" onClick={handleBuyNowClick}>
            <FaRocket /> Contact Us for Pricing
          </button>
        </div>
      </section>
    </div>
  );
};

const FeatureRow = ({ title, imageSrc, points, icon }) => {
  return (
    <div className="tss-feature-row">
      <div className="tss-feature-content">
        <h2>{icon} {title}</h2>
        <ul className="tss-feature-list">
          {points.map((point, index) => (
            <li key={index} className="tss-feature-item">
              <FaCheckCircle />
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="tss-feature-visual">
        <div className="tss-feature-image-wrapper">
          <img src={imageSrc} alt={title} />
        </div>
      </div>
    </div>
  );
};

export default TallySoftware;

